import { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import AppRoutes from 'constants/AppRoutes';
import useUpdateStatusSection from 'hooks/useUpdateStatusSection';
import { useGetSingleMAInitiateQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import {
  useGetDashboardTopicsQuery,
  useGetRespondentImpactQuery,
  useGetSurveysQuery, useLazyGetInsightsDashboardQuery,
  useSetRespondentImpactMutation,
} from 'store/slices/survey/apis/surveyApi';

import {
  COEFFICIENT, COEFFICIENT_KEY, TEMPLATE,
} from '../../../constants/formFields';
import { BLOCKER_STATES } from '../../../constants/general';
import { SECTION_TYPE, STATUS_SECTIONS, SURVEY_STATUS } from '../../../constants/materialAssessment';
import { useUserMeQuery } from '../../../store/slices/user/apis/user';
import useInsightsDashboardForm from './hooks/useInsightsDashboardForm';
import MaterialAssessmentInsightsDashboardView from './MaterialAssessmentInsightsDashboardView';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IInsightsDashboardTemplates from './interfaces/IInsightsDashboardTemplates';

function MaterialAssessmentInsightsDashboard() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [templatesData, setTemplatesData] = useState<IInsightsDashboardTemplates[]>([]);
  const [isSliderWrapper, setSliderWrapper] = useState(false);
  const [isBackModalOpen, setIsBackModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [lastAppliedKey, setLastAppliedKey] = useState<string | null>(null);
  const [options, setOptions] = useState<IOption[]>([]);

  const { data: singleMA } = useGetSingleMAInitiateQuery({ id: recordId || '' });

  const {
    control,
    watch,
    reset,
    setValue,
    getValues,
    isDirty,
  } = useInsightsDashboardForm();

  const { fields, update } = useFieldArray({
    control,
    name: COEFFICIENT,
    keyName: COEFFICIENT_KEY,
  });

  const { updateStatus } = useUpdateStatusSection({
    recordId,
    type: SECTION_TYPE.INSIGHT_DASHBOARD,
    active: true,
    status: STATUS_SECTIONS.COMPLETED,
  });

  const currentValues = watch();

  const {
    template: templateId = null,
    topic: topicId,
    stakeholder,
    coefficient,
    applyCoefficient,
    valueChainSection,
  } = currentValues;

  const currentKey = JSON.stringify({
    templateId: templateId ?? null,
    topicId: topicId ?? null,
    stakeholder: Array.isArray(stakeholder) ? stakeholder : [],
    applyCoefficient: applyCoefficient ?? null,
    valueChainId: valueChainSection ? valueChainSection.value : null,
  });

  const hasTemplate = templateId !== null && templateId !== undefined;
  const hasTopic = topicId !== null && topicId !== undefined && Boolean(topicId);
  const hasStakeholder = Array.isArray(stakeholder) ? stakeholder.length > 0 : Boolean(stakeholder);
  const hasCoefficient = applyCoefficient !== null && applyCoefficient !== undefined && applyCoefficient;
  const hasValueChain = valueChainSection && valueChainSection.value !== '';

  const showActionButtons = (hasTemplate || hasTopic || hasStakeholder || hasCoefficient || hasValueChain)
    && currentKey !== lastAppliedKey;

  const { data: userData } = useUserMeQuery();
  const companyId = userData?.data?.companyId;

  const { data } = useGetSurveysQuery({
    maId: Number(recordId),
    status: SURVEY_STATUS.DONE,
  });
  const { items } = data?.data || {};

  const [fetchDashboard, { data: dashboardData }] = useLazyGetInsightsDashboardQuery();

  const { data: dashboardTopics = [] } = useGetDashboardTopicsQuery(
    { questionListId: templateId! },
    { skip: !templateId },
  );

  const { data: coefficientData = [] } = useGetRespondentImpactQuery(
    { companyId },
    { skip: !isSliderWrapper },
  );

  const [setRespondentImpact, { isLoading: isSetImpactLoading }] = useSetRespondentImpactMutation();

  const handleClearAll = () => {
    reset({ [TEMPLATE]: undefined });
    setLastAppliedKey(null);
  };

  const handleGenerate = () => {
    if (!templateId) return;

    fetchDashboard({
      questionListId: Number(templateId),
      ...(topicId !== undefined && topicId !== null ? { topicId } : {}),
      ...((companyId && applyCoefficient) ? { companyId } : {}),
      ...(Array.isArray(stakeholder) && stakeholder.length ? { respondentType: stakeholder } : {}),
      valueChainMapId: Number(valueChainSection.value),
    });
    setLastAppliedKey(currentKey);
  };

  const handleSaveCoefficients = async () => {
    if (companyId) {
      await setRespondentImpact({ impacts: coefficient || [], companyId }).unwrap();
      reset();
    }
  };

  const handleBack = () => {
    if (isDirty) {
      setIsBackModalOpen(true);
    } else {
      setSliderWrapper(false);
    }
  };

  const handleBackConfirm = () => {
    setSliderWrapper(false);
    setIsBackModalOpen(false);
    reset();
  };

  const handleResetConfirm = () => {
    const fresh = (coefficientData ?? [])
      .map((i) => ({ id: i.id, impact: +i.impact }))
      .sort((a, b) => a.id - b.id);

    reset({ ...getValues(), [COEFFICIENT]: fresh }, { keepDefaultValues: false });
    setIsResetModalOpen(false);
  };

  const handleCancelDashboard = () => {
    navigate(AppRoutes.materialityAssessment);
  };

  const handleNext = () => {
    updateStatus();
    navigate(AppRoutes.materialityAssessmentTopicRefinementII.replace(':recordId', recordId || ''));
  };

  const handlePrev = () => {
    navigate(AppRoutes.materialityAssessmentRoundtables.replace(':recordId', recordId || ''));
  };

  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty
    && currentLocation.pathname !== nextLocation.pathname
    && !isBackModalOpen && isSliderWrapper);

  useEffect(() => {
    if (blocker.state === BLOCKER_STATES.blocked) {
      if (isDirty) {
        setIsBackModalOpen(true);
      } else {
        blocker.proceed?.();
      }
    }
  }, [blocker, isDirty]);

  useEffect(() => {
    if (items && items.length > 0) {
      const filteredTemplates = items.filter((item) => item.standardId);
      const templates = filteredTemplates.map((item) => ({
        id: item.id,
        value: item.name,
        label: item.name,
      }));
      setTemplatesData(templates);
    }
  }, [items]);

  useEffect(() => {
    if (isSliderWrapper) {
      setValue(
        COEFFICIENT,
        coefficientData.map((item) => ({ id: item.id, impact: +item.impact })) ?? [],
      );
    }
  }, [isSliderWrapper, coefficientData, setValue]);

  useEffect(() => {
    if (singleMA) {
      const vcList = singleMA?.data?.valueChainMaps?.map(
        (itemVC) => ({ label: itemVC.name, value: `${itemVC.id}` }),
      ) || [];
      setOptions([{ label: 'All', value: '' }, ...vcList]);
    }
  }, [singleMA]);

  return (
    <MaterialAssessmentInsightsDashboardView
      control={control}
      dashboardData={dashboardData}
      templatesData={templatesData}
      dashboardTopics={dashboardTopics}
      coefficientData={coefficientData}
      showActionButtons={showActionButtons}
      isSliderWrapper={isSliderWrapper}
      setSliderWrapper={setSliderWrapper}
      isBackModalOpen={isBackModalOpen}
      isResetModalOpen={isResetModalOpen}
      setIsBackModalOpen={setIsBackModalOpen}
      onHandleBack={handleBack}
      onHandleBackConfirm={handleBackConfirm}
      onHandleReset={setIsResetModalOpen}
      onHandleResetConfirm={handleResetConfirm}
      handleCancelDashboard={handleCancelDashboard}
      setValue={setValue}
      fields={fields}
      update={update}
      onClearAll={handleClearAll}
      onGenerate={handleGenerate}
      onSaveCoefficients={handleSaveCoefficients}
      isSetImpactLoading={isSetImpactLoading}
      handleNext={handleNext}
      options={options}
      handlePrev={handlePrev}
    />
  );
}

export default MaterialAssessmentInsightsDashboard;
